import { AnimatedContentLoader, Input } from '@components'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  DropdownWrapper,
  Item,
  ItemColumn,
  StyledBusinessDropdown
} from './BusinessSearchDropdown.styled'
import { useFormContext, useWatch } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'
import { IYTJCompanyDetails, fetchYtjCompanyDetails } from '@query/integrations/ytj'
import { createPortal } from 'react-dom'
import { AnimatePresence } from 'framer-motion'
import { autoUpdate, flip, offset, size, useDismiss, useFloating } from '@floating-ui/react'
import { TIME_CONSTANTS } from '@root/constants'

interface IProps {
  onCopy: (data: IYTJCompanyDetails) => void
  required: boolean
}

const businessIdRegex = /^\d{7}-\d$/

const BusinessIdDropdown: React.FC<IProps> = ({ onCopy, required }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [hasUserTyped, setHasUserTyped] = useState(false)
  const [focusIndex, setFocusIndex] = useState(-1)
  const { t } = useTranslation()

  const {
    register,
    control,
    formState: { errors }
  } = useFormContext()

  const rootRef = useRef<HTMLElement>(document.getElementById('dropdown-root'))

  const contactBusinessId = useWatch({
    control,
    name: 'contact_business_id'
  })

  const {
    data: resultsFromFetch,
    isLoading,
    isError
  } = useQuery({
    queryKey: ['ytj', 'company_details', { contactBusinessId }],
    queryFn: () => fetchYtjCompanyDetails({ businessId: contactBusinessId }),
    enabled: hasUserTyped && businessIdRegex.test(contactBusinessId),
    staleTime: TIME_CONSTANTS.SECONDS_IN_MINUTE * TIME_CONSTANTS.MS_IN_SECOND
  })

  const businessName = resultsFromFetch?.trade_name?.name
  const businessId = resultsFromFetch?.business_id_details?.business_id
  const hasResult = resultsFromFetch && businessName

  const handleClose = () => {
    setIsOpen(false)
    setHasUserTyped(false)
    setFocusIndex(-1)
  }

  useEffect(() => {
    if (businessIdRegex.test(contactBusinessId) && hasResult && hasUserTyped) {
      setIsOpen(true)
    } else {
      setIsOpen(false)
    }
  }, [resultsFromFetch, contactBusinessId, hasUserTyped])

  const onKeyDown = e => {
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        if (hasResult) setFocusIndex(0)
        break
      }

      case 'Enter': {
        if (focusIndex === 0 && resultsFromFetch) {
          onCopy(resultsFromFetch)
        }
        setFocusIndex(-1)
        setIsOpen(false)
        setHasUserTyped(false)
        e.stopPropagation()
        e.preventDefault()
        break
      }

      case 'Escape': {
        handleClose()
        break
      }
    }
  }

  const { context, x, y, refs, strategy } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    placement: 'bottom-start',
    middleware: [
      offset(0),
      flip(),
      size({
        apply: ({ availableWidth, availableHeight, elements }) => {
          Object.assign(elements.floating.style, {
            width: `${Math.min(500, availableWidth - 48)}px`,
            maxHeight: `${Math.min(6000, availableHeight - 48)}px`
          })
        }
      })
    ]
  })

  useDismiss(context)

  const registeredBusinessId = {
    ...register('contact_business_id', {
      shouldUnregister: true,
      required: required ? { value: true, message: t('validation.requiredWhenInvoicing') } : false
    })
  }

  return (
    <DropdownWrapper ref={refs.setReference}>
      <Input
        label={t('contacts.form.businessId')}
        onKeyDown={onKeyDown}
        errors={errors?.contact_business_id}
        {...registeredBusinessId}
        required={required}
        onChange={e => {
          registeredBusinessId.onChange(e)
          setHasUserTyped(true)
        }}
        onBlur={e => {
          registeredBusinessId.onBlur(e)
          setIsOpen(false)
          setHasUserTyped(false)
        }}
      />

      {createPortal(
        <AnimatePresence>
          {isOpen && (
            <StyledBusinessDropdown
              ref={refs.setFloating}
              style={{
                top: y || 0,
                left: x || 0,
                position: strategy
              }}
            >
              <AnimatedContentLoader
                isLoading={isLoading}
                isEmpty={!hasResult}
                isEmptyDescription={t('contacts.form.businessAutoFill.isEmpty')}
                isError={isError}
                errorDescription={t('general.error')}
              >
                {hasResult && (
                  <Item
                    key={businessId}
                    className={focusIndex === 0 && 'keyboard-focused'}
                    onTouchStart={() => {
                      onCopy(resultsFromFetch)
                      setIsOpen(false)
                      setHasUserTyped(false)
                    }}
                    onMouseDown={() => {
                      onCopy(resultsFromFetch)
                      setIsOpen(false)
                      setHasUserTyped(false)
                    }}
                  >
                    <ItemColumn className="description">{businessName}</ItemColumn>
                    <ItemColumn>{businessId}</ItemColumn>
                  </Item>
                )}
              </AnimatedContentLoader>
            </StyledBusinessDropdown>
          )}
        </AnimatePresence>,
        rootRef.current
      )}
    </DropdownWrapper>
  )
}

export default BusinessIdDropdown
